import { BasePageComponent } from "../../../_shared/components/page/base-page.component";
import { ServiceLibraryService } from "./services/service-library.service";
import { ErrorHandlerService } from "../../../_shared/services/error-handler.service";
import { MakeOption, ServiceSchedule, ServiceLibraryDetailsPayload, YearsRequest, AssetModel, ServiceTypesWebRequest, ServiceLibraryServiceTypeOption } from "./models/service-library.models";
import { ScheduleType } from "../../_shared/enums/service-schedule-type.enum";
import { ServiceTypes } from "../../_shared/enums/service-type.enum";
import { Directive, inject } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { Subject } from "rxjs";
import { FilterService } from "../../../_shared/services/filter.service";

@Directive()
export abstract class BaseServiceLibraryComponent extends BasePageComponent {
  private static readonly allServiceTypeOptions: ServiceLibraryServiceTypeOption[] = [
    { label: 'serviceLibrary.serviceTypes.minor', value: ServiceTypes.Minor },
    { label: 'serviceLibrary.serviceTypes.intermediate', value: ServiceTypes.Intermediate },
    { label: 'serviceLibrary.serviceTypes.major', value: ServiceTypes.Major },
  ];

  protected readonly transloco: TranslocoService = inject(TranslocoService);
  protected serviceLibraryService: ServiceLibraryService = inject(ServiceLibraryService);
  protected errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  protected filterService: FilterService = inject(FilterService);
  private readonly cancelBaseSubject = new Subject<void>();

  schedule: ServiceSchedule;
  isLoading: boolean = true;
  isLoadingMakes: boolean = false;
  isLoadingModels: boolean = false;
  isLoadingYears: boolean = false;
  isLoadingServiceTypes: boolean = false;
  models: AssetModel[] = [];
  makes: MakeOption[] = [];
  years: number[] = [];
  serviceTypeOptions = [...BaseServiceLibraryComponent.allServiceTypeOptions];

  selectedMake: MakeOption | null = null;
  selectedModel: AssetModel | null = null;
  selectedYear: number | null = null;
  selectedServiceType: ServiceTypes | null = null;

  constructor() {
    super();

    const navState = history.state as { schedule?: ServiceSchedule; payload?: ServiceLibraryDetailsPayload };
    this.schedule = navState?.schedule ?? navState?.payload?.schedule as ServiceSchedule;
    if (!this.schedule) {
      this.router.navigate(['/asset-management/service-library']);
      return;
    }

    this.isLoadingMakes  = this.schedule.serviceScheduleTypeId === ScheduleType.Custom;
    this.isLoadingModels = this.schedule.serviceScheduleTypeId === ScheduleType.OEM;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.shouldAutoLoadFilters()) {
      void this.loadFiltersAsync();
    }
  }

  get yearDisabled(): boolean {
    return !this.selectedModel;
  }

  get serviceTypeDisabled(): boolean {
    return this.schedule.serviceScheduleTypeId === ScheduleType.OEM && !this.selectedYear;
  }

  get canProceed(): boolean {
    return this.schedule.serviceScheduleTypeId === ScheduleType.Custom || !!(this.selectedModel && this.selectedYear && this.selectedServiceType);
  }

  get makeLabel(): string {
    return this.selectedMake?.name ?? this.transloco.translate('serviceLibrary.filterPage.make');
  }

  get modelLabel(): string {
    return this.selectedModel?.name ?? this.transloco.translate('serviceLibrary.filterPage.model');
  }

  get yearLabel(): string {
    return this.selectedYear?.toString() ?? this.transloco.translate('serviceLibrary.filterPage.year');
  }

  get serviceTypeLabel(): string {
    return this.selectedServiceType != null
      ? this.translateServiceTypeLabel(this.selectedServiceType)
      : this.transloco.translate('serviceLibrary.filterPage.serviceType');
  }

  protected async loadFiltersAsync(): Promise<void> {
    if (this.schedule.serviceScheduleTypeId === ScheduleType.OEM) await this.fetchModelOptionsAsync(this.schedule.id);
  }

  protected async fetchModelOptionsAsync(serviceScheduleId: number): Promise<void> {
    this.isLoadingModels = true;
    this.cancelBaseSubject.next();

    try {
      const accountIds = this.filterService.filter?.accountIds ?? [];
      const response = await this.serviceLibraryService.getModelOptionsAsync(serviceScheduleId, { accountIds }, this.cancelBaseSubject);
      this.models = response.data ?? [];
      this.isLoadingModels = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.models = [];
          this.years = [];
          this.selectedModel = null;
          this.selectedYear = null;
          this.selectedServiceType = null;
          this.serviceTypeOptions = [];
          this.isLoadingModels = false;
        }
      });
    }
  }

  protected async fetchYearOptions(): Promise<void> {
    this.isLoadingYears = true;

    try {
      if (!this.selectedModel) {
        this.years = [];
        this.isLoadingYears = false;
        return;
      }

      this.cancelBaseSubject.next();

      const request: YearsRequest = {
        accountIds: this.filterService.filter?.accountIds ?? [],
        modelId: this.selectedModel.id
      };

      const response = await this.serviceLibraryService.getYearsOptionsAsync(this.schedule.id, request, this.cancelBaseSubject);
      this.years = response.data ?? []
      this.isLoadingYears = false;
    } catch(error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.years = [];
          this.selectedYear = null;
          this.selectedServiceType = null;
          this.serviceTypeOptions = [];
          this.isLoadingYears = false;
        }
      });
    }
  }

  protected async fetchOemAssingedServiceTypes(): Promise<void> {
    this.isLoadingServiceTypes = true;
    try {
      if (!this.selectedModel || this.selectedYear == null) {
        this.serviceTypeOptions = [];
        this.isLoadingServiceTypes = false;
        return;
      }

      this.cancelBaseSubject.next();

      const request: ServiceTypesWebRequest = {
        accountIds: this.filterService.filter?.accountIds ?? [],
        modelId: this.selectedModel.id,
        year: this.selectedYear
      };
      const response = await this.serviceLibraryService.GetServiceTypesByServiceScheduleModelAndYear(this.schedule.id, request, this.cancelBaseSubject);
      const allowedServiceTypeIds = response.data ?? [];

      this.serviceTypeOptions = BaseServiceLibraryComponent.allServiceTypeOptions.filter((x) =>
        allowedServiceTypeIds.includes(x.value)
      );
      this.isLoadingServiceTypes = false;
    } catch(error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.selectedServiceType = null;
          this.serviceTypeOptions = [];
          this.isLoadingServiceTypes = false;
        }
      });
    }
  }

  onSelectModel(model: AssetModel): void {
    this.selectedModel = model;
    this.selectedYear = null;
    this.selectedServiceType = null;
    this.years = [];
    this.serviceTypeOptions = [];
    void this.fetchYearOptions();
  }

  onSelectYear(year: number): void {
    if (this.yearDisabled) return;

    this.selectedYear = year;
    this.selectedServiceType = null;
    this.serviceTypeOptions = [];

    void this.fetchOemAssingedServiceTypes();
  }

  proceed(): void {
    const payload: ServiceLibraryDetailsPayload = {
      makeId: this.schedule.serviceScheduleTypeId === ScheduleType.OEM ? this.schedule.makeId ?? 0 : this.selectedMake?.id ?? 0,
      modelId: this.selectedModel?.id ?? null,
      year: this.selectedYear ?? null,
      serviceType: this.selectedServiceType ?? ServiceTypes.Minor,
      schedule: this.schedule,
      models: [...this.models],
      years: [...this.years],
      serviceTypeOptions: [...this.serviceTypeOptions]
    }

    this.updatePageFilterState();
    this.navigateToDetails(payload);
  }

  protected navigateToDetails(payload: ServiceLibraryDetailsPayload): void {
    this.router.navigate([`/asset-management/service-library/schedule/${this.schedule.id}/details`], { state: { payload } });
  }

  protected shouldAutoLoadFilters(): boolean {
    return true;
  }

  /** Resolves a service-type option label, which is stored as a translation key. */
  protected translateServiceTypeLabel(serviceType: ServiceTypes): string {
    const key = BaseServiceLibraryComponent.allServiceTypeOptions.find((option) => option.value === serviceType)?.label;
    return key ? this.transloco.translate(key) : ServiceTypes[serviceType];
  }

  protected abstract onSelectServiceType(serviceType: ServiceTypes): void;
  protected abstract onBackButtonClick(): void;
}